import styled from "@emotion/styled";
import React, { useState } from "react";
import { fontTitle } from "src/design-system/styles/fonts";
import { useDisableScroll } from "src/hooks/scroll";
import { Hamburger, breakpointNavBarFull } from "../nav-bar/Hamburger";
import { compassColors } from "src/utils/styling";
import Button from "src/design-system/components/Button/Button";
import { NavBarItem } from "../nav-bar/Styles";
import {
  breakpointDesktopOnly,
  breakpointWideDesktopOnly,
} from "src/design-system/styles/breakpoints";
import { FullWidth } from "../core/BodyLayout";
import { MenuBackground } from "../nav-bar/MenuBackground";
import MedLogo from "./images/medLogoWhite.svg";
import { css } from "@emotion/react";
import { delayedFadeIn } from "../animation/fade";
import { trackEvent, trackBuyflowEvent } from "src/utils/api/tracker";
import i18n from "@i18n";
import { ClinicalNavBarDropdown } from "./ClinicalNavBarDropdown";
import ArrowRightIcon from "./images/arrow-right.svg";
import { loginLink } from "@utils/navigation/login";
import { Text, TranslationProps } from "@components/core/Text";

const NavBar = styled(FullWidth)`
  display: flex;
  flex-direction: row;
  min-height: 50px;
  align-items: center;
  @media ${breakpointDesktopOnly} {
    min-height: 90px;
  }
  @media ${breakpointNavBarFull} {
    justify-content: space-between;
  }
`;

const NoomLogo = styled.img`
  position: relative;
  left: calc(50vw - 130px);
  height: 16px;
  @media ${breakpointNavBarFull} {
    left: 0;
    margin-left: 120px;
    height: 20px;
  }
`;

const NavBarHamburger = styled(NavBarItem)`
  border: none;
  color: white;
`.withComponent(Hamburger);

const NavBarCTA = styled(Button, {
  shouldForwardProp: (prop) => prop !== "menuOpen",
})<{ menuOpen?: boolean }>`
  width: 190px;
  min-height: 30px;
  height: 40px;
  display: none;
  background-color: ${compassColors.white};
  &:hover {
    background-color: ${compassColors.white};
  }
  color: ${compassColors.blueberry};
  @media ${breakpointNavBarFull} {
    display: block;
  }
  @media ${breakpointWideDesktopOnly} {
    margin-right: 120px;
  }
  ${({ menuOpen }) =>
    menuOpen &&
    css`
      display: block;
      margin-top: 14px;
      height: 50px;
      background-color: ${compassColors.lagoon};
      &:hover {
        background-color: ${compassColors.lagoon};
      }
      color: ${compassColors.white};
    `}
`;

const MenuURLsContainer = styled.div`
  display: flex;
  flex-direction: row;
  margin-left: auto;
  display: none;
  @media ${breakpointNavBarFull} {
    display: block;
  }
`;

const MenuURL = styled.a`
  ${fontTitle}
  text-decoration: none;
  margin: 0 14px;
  color: ${compassColors.white};
`;

const MenuURLMobile = styled.a`
  ${fontTitle}
  text-decoration: none;
  text-transform: uppercase;
  margin: 10px 0;
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
`;

const MenuURLsContainerMobile = styled("div", {
  shouldForwardProp: (prop) => prop !== "menuOpen",
})<{ menuOpen: boolean }>`
  display: flex;
  flex-direction: column;
  ${({ menuOpen }) =>
    menuOpen
      ? css`
          animation: ${delayedFadeIn} 1.5s;
          z-index: 6;
          position: fixed;
          top: 80px;
          box-sizing: border-box;
          padding: 0 24px;
          width: 100%;
        `
      : css`
          display: none;
        `}
`;

const ArrowImage = styled.img`
  margin: 0;
  padding-left: 5px;
`;

export function ClinicalNavBar({
  gotoNextPage,
  gotoWeightLandingPage,
  showNoomForWorkLinks = true,
  showLoginLink = true,
  noomWeightText = { i18nKey: "introClinical:navBar:noomWeight" },
}: {
  gotoNextPage: () => void;
  gotoWeightLandingPage: () => void;
  showNoomForWorkLinks?: boolean;
  showLoginLink?: boolean;
  noomWeightText?: TranslationProps;
}) {
  const [menuOpen, setMenuOpen] = useState(false);

  useDisableScroll(menuOpen);

  return (
    <NavBar>
      <NavBarHamburger
        active={menuOpen}
        onClick={() => {
          setMenuOpen(!menuOpen);
        }}
      />
      <MenuURLsContainerMobile menuOpen={menuOpen}>
        {showNoomForWorkLinks && <ClinicalNavBarDropdown menuOpen={menuOpen} />}
        <MenuURLMobile
          href="https://noom.com/"
          onClick={(event) => {
            event.preventDefault();
            trackEvent("ClickedNWNavLink");
            gotoWeightLandingPage();
          }}
        >
          <Text {...noomWeightText} />
          <ArrowImage src={ArrowRightIcon} />
        </MenuURLMobile>
        {showLoginLink && (
          <MenuURLMobile
            href={loginLink}
            onClick={() => trackEvent("ClickedLoginNavLink")}
          >
            {i18n.t("introClinical:navBar:login")}
            <ArrowImage src={ArrowRightIcon} />
          </MenuURLMobile>
        )}
        <NavBarCTA
          borderRadius="32px"
          onClick={() => {
            trackBuyflowEvent("BuyflowButtonClicked", {
              pageId: "noomMedLP",
              buttonName: "navBar",
            });
            gotoNextPage();
          }}
          menuOpen={menuOpen}
        >
          {i18n.t("introClinical:navBar:cta")}
        </NavBarCTA>
      </MenuURLsContainerMobile>
      <MenuBackground open={menuOpen} reversePosition />
      <NoomLogo
        src={MedLogo}
        onClick={() =>
          trackBuyflowEvent("BuyflowImageClicked", {
            imageName: "logo",
            pageId: "noomMedLP",
          })
        }
      />
      <MenuURLsContainer>
        {showNoomForWorkLinks && <ClinicalNavBarDropdown menuOpen={menuOpen} />}
        <MenuURL
          href="https://noom.com/"
          onClick={(event) => {
            event.preventDefault();
            trackEvent("ClickedNWNavLink");
            gotoWeightLandingPage();
          }}
        >
          <Text {...noomWeightText} />
        </MenuURL>
        {showLoginLink && (
          <MenuURL
            href={loginLink}
            onClick={() => trackEvent("ClickedLoginNavLink")}
          >
            {i18n.t("introClinical:navBar:login")}
          </MenuURL>
        )}
      </MenuURLsContainer>
      <NavBarCTA
        borderRadius="32px"
        onClick={() => {
          trackBuyflowEvent("BuyflowButtonClicked", {
            pageId: "noomMedLP",
            buttonName: "navBar",
          });
          gotoNextPage();
        }}
      >
        {i18n.t("introClinical:navBar:cta")}
      </NavBarCTA>
    </NavBar>
  );
}
